import { useEffect, useRef } from 'react';
import type { ProductListItem } from 'types/capi/shared';

import ProductCardImpressionEvent from '~/events/ProductCardImpressionEvent';
import useUser from '~/hooks/useUser';

const VIEWABILITY_THRESHOLD = 0.5;

type Options = {
  isBot: boolean;
  modulePosition?: number;
  moduleType?: string;
  position?: number;
  product: ProductListItem;
};

function useProductCardImpression({
  isBot,
  modulePosition,
  moduleType,
  position,
  product
}: Options) {
  const { guestId } = useUser();
  const elementRef = useRef<HTMLDivElement | null>(null);
  const hasTracked = useRef(false);

  useEffect(() => {
    if (isBot || hasTracked.current) return;

    const element = elementRef.current;

    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            hasTracked.current ||
            !entry.isIntersecting ||
            entry.intersectionRatio < VIEWABILITY_THRESHOLD
          ) {
            return;
          }

          hasTracked.current = true;

          new ProductCardImpressionEvent({
            guestId,
            isBot,
            modulePosition,
            moduleType,
            position,
            product
          }).track();

          observer.disconnect();
        });
      },
      { threshold: VIEWABILITY_THRESHOLD }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [guestId, isBot, modulePosition, moduleType, position, product]);

  return elementRef;
}

export default useProductCardImpression;
